import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { axiosServiceInstance } from '@onesource/services';
import { useRef, useState } from 'react';
import { MdAttachFile, MdClose, MdCloudUpload, MdDescription, MdImage, MdVideoLibrary } from 'react-icons/md';

import { FORM_CONTENT_MAX_WIDTH } from './rich-content-sx';

const ACCEPTED_TYPES = 'image/*,video/*,application/pdf';
const MAX_FILE_SIZE_MB = 100;

interface FileUploadQuestionProps {
  fileUrls: string[];
  onChange: (fileUrls: string[]) => void;
  disabled?: boolean;
}

const getPresignedUrl = async (
  key: string,
  contentType: string,
  contentLength: number,
): Promise<{ uploadUrl: string; publicUrl: string | null }> => {
  const res = await axiosServiceInstance.get<{ uploadUrl: string; publicUrl: string | null }>(
    '/community-hub/forms/upload/presigned-url',
    { params: { key, contentType, contentLength } },
  );
  return res.data;
};

const uploadFile = async (file: File): Promise<string> => {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const key = `${Date.now()}-${safeName}`;
  const { uploadUrl, publicUrl } = await getPresignedUrl(key, file.type, file.size);
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
  return publicUrl || uploadUrl.split('?')[0];
};

const fileNameFromUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname;
    const last = path.split('/').filter(Boolean).pop() || url;
    return decodeURIComponent(last.replace(/^\d+-/, ''));
  } catch {
    return url.split('/').pop() || url;
  }
};

const isImageUrl = (url: string): boolean => /\.(jpg|jpeg|png|gif|webp|avif|svg)(\?|$)/i.test(url);
const isVideoUrl = (url: string): boolean => /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url);
const isPdfUrl = (url: string): boolean => /\.pdf(\?|$)/i.test(url);

const FileTypeIcon = ({ url }: { url: string }) => {
  if (isImageUrl(url)) {
    return <MdImage size={20} color="#ff4d6d" />;
  }
  if (isVideoUrl(url)) {
    return <MdVideoLibrary size={20} color="#ff4d6d" />;
  }
  if (isPdfUrl(url)) {
    return <MdDescription size={20} color="#ff4d6d" />;
  }
  return <MdAttachFile size={20} color="#ff4d6d" />;
};

export const FileUploadQuestion = ({ fileUrls, onChange, disabled = false }: FileUploadQuestionProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setError(null);

    const file = files[0];
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB limit.`);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);
    try {
      const url = await uploadFile(file);
      // Replace any prior selection — this question accepts a single file.
      onChange([url]);
    } catch (error) {
      console.error('File upload failed:', error);
      setError(error instanceof Error ? error.message : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange([]);
  };

  return (
    <Box sx={{ maxWidth: FORM_CONTENT_MAX_WIDTH, mx: 'auto' }}>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        hidden
        onChange={e => handleFiles(e.target.files)}
        disabled={disabled || isUploading}
      />

      <Box
        onClick={() => !disabled && !isUploading && inputRef.current?.click()}
        sx={{
          border: '2px dashed',
          borderColor: disabled ? 'rgba(0,0,0,0.12)' : '#ffb6c4',
          bgcolor: '#fff8f9',
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1,
          cursor: disabled || isUploading ? 'default' : 'pointer',
          transition: 'all 0.15s',
          '&:hover': disabled || isUploading ? {} : { borderColor: '#ff4d6d', bgcolor: '#fff0f3' },
        }}
      >
        {isUploading ? (
          <CircularProgress size={32} sx={{ color: '#ff4d6d' }} />
        ) : (
          <MdCloudUpload size={36} color="#ff4d6d" />
        )}
        <Typography sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          {isUploading ? 'Uploading…' : fileUrls.length > 0 ? 'Click to replace' : 'Click to upload'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Photos, videos, or PDFs · up to {MAX_FILE_SIZE_MB} MB
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={e => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          disabled={disabled || isUploading}
          sx={{
            mt: 1,
            textTransform: 'none',
            borderColor: '#ff4d6d',
            color: '#ff4d6d',
            '&:hover': { borderColor: '#e63956', bgcolor: 'rgba(255,77,109,0.06)' },
          }}
        >
          {fileUrls.length > 0 ? 'Replace file' : 'Choose file'}
        </Button>
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}

      {fileUrls.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {fileUrls.map(url => (
            <Box
              key={url}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              {isImageUrl(url) ? (
                <Box
                  component="img"
                  src={url}
                  alt=""
                  sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }}
                />
              ) : (
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 1,
                    bgcolor: '#fff0f3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <FileTypeIcon url={url} />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: 500,
                    color: '#1a1a1a',
                    textDecoration: 'none',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:hover': { color: '#ff4d6d', textDecoration: 'underline' },
                  }}
                >
                  {fileNameFromUrl(url)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Click to preview
                </Typography>
              </Box>
              {!disabled && (
                <IconButton size="small" onClick={handleRemove}>
                  <MdClose size={18} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
